import React, { useState, useEffect } from 'react';
import { Modal, Button, Typography, Space, message, Skeleton } from 'antd';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { ArrowLeftOutlined } from '@ant-design/icons';
import style from './index.module.scss';
import { toastText } from 'utils/utils';
const { Text, Link } = Typography;

interface ConfirmationModalProps {
	visible: boolean;
	onCancel: () => void;
	onConfirm: () => void;
}

interface Account {
	AccountUid: string;
	AccountName: string;
	PrimaryContactEmail: string;
	IsPrimary: boolean;
	hasNewPlan: boolean;
}

// Define a type for token payload if needed
interface OutsetaTokenPayload {
	PersonUid: string;
	[key: string]: any; // other fields
}

const getPersonIdFromToken = (): string | null => {
	const token = localStorage.getItem('outsetaAccessToken');
	if (!token) return null;

	try {
		const decoded: OutsetaTokenPayload = jwtDecode(token);
		return decoded.nameid || null;
	} catch (error) {
		console.error('Failed to decode token', error);
		return null;
	}
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
	visible,
	onCancel,
	onConfirm,
}) => {
	const [accounts, setAccounts] = useState<any[]>([]);
	const [selectedUid, setSelectedUid] = useState<string | null>(null);
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		if (visible) {
			fetchData();
		}
	}, [visible]);

	const fetchData = async () => {
		try {
			setLoading(true);
			const personId = getPersonIdFromToken();
			if (!personId) {
				message.error('User token not found or invalid.');
				return;
			}
			const personResponse = await axios.get(
				`${process.env.REACT_APP_OUTSETA_BASE_URL}/crm/people/${personId}?fields=*,PersonAccount.*,PersonAccount.Account.*,PersonAccount.Account.PrimaryContact.*,PersonAccount.Account.CurrentSubscription.*,PersonAccount.Account.CurrentSubscription.Plan.*`,
				{
					headers: {
						Authorization: `Outseta ${process.env.REACT_APP_OUTSETA_API_KEY}:${process.env.REACT_APP_OUTSETA_API_SECRET}`,
						'Content-Type': 'application/json',
					},
				}
			);
			const personAccounts = personResponse.data.PersonAccount || [];
			const newPlanUid = process.env.REACT_APP_OUTSETA_PLAN_UID_2;

			if (!newPlanUid) {
				console.warn('REACT_APP_OUTSETA_PLAN_UID_2 is not set in environment variables');
				setLoading(false);
				return;
			}

			console.log('personAccounts', personAccounts);

			// Map all accounts (no filtering - show all accounts)
			const mappedAccounts = personAccounts
				.map((pa: any) => {
					// Use CurrentSubscription instead of filtering subscriptions
					const currentSubscription = pa.Account?.CurrentSubscription;

					// Check if CurrentSubscription exists and its Plan UID matches the new plan UID
					const hasNewPlan = currentSubscription?.Plan?.Uid === newPlanUid;

					return {
						IsPrimary: pa.IsPrimary,
						AccountUid: pa.Account.Uid,
						AccountName: pa.Account.Name,
						PrimaryContactEmail: pa.Account?.PrimaryContact?.Email,
						hasNewPlan: hasNewPlan,
						currentSubscription: currentSubscription,
					};
				});

			if (mappedAccounts.length === 0) {
				toastText('No accounts found. Please contact support.', 'warning');
			}

			setAccounts(mappedAccounts);
		} catch (error) {
			console.error('Error fetching data:', error);
			message.error('Failed to load account details.');
		} finally {
			setLoading(false);
		}
	};

	const handleSelectChange = (account: Account) => {
		setSelectedUid(account.AccountUid);
		setSelectedAccount(account);
	};

	const handleConfirm = () => {
		if (!selectedUid || !selectedAccount) {
			toastText('Please select an account first', 'warning');
			return;
		}
		// Only allow confirmation if account is primary and has new plan
		if (!selectedAccount.IsPrimary) {
			return;
		}
		if (!selectedAccount.hasNewPlan) {
			return;
		}
		// Set flag for new plan
		localStorage.setItem('isNewOutsetaPlan', 'true');
		// Store the Outseta account UID for connection (consistent name for all flows)
		localStorage.setItem('outsetaAccountUidForConnection', selectedUid);
		onConfirm();
	};

	const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

	const handleBack = () => {
		setSelectedUid(null);
		setSelectedAccount(null);
	};

	useEffect(() => {
		if (visible) {
			setSelectedAccount(null);
		}
	}, [visible]);

	return (
		<Modal
			open={visible}
			onCancel={onCancel}
			footer={null}
			width={550}
			zIndex={9999}
		>
			<Space direction="vertical" style={{ width: '100%', padding: '5px' }}>
				{loading ? (
					<Skeleton active paragraph={{ rows: 2 }} />
				) : (
					<>
						{!selectedAccount ? (
							<span
								style={{
									maxHeight: 'vh', // Adjust this value as needed
									overflowY: 'auto',
									paddingRight: '10px', // optional: adds space to avoid content hiding behind scrollbar
								}}
							>
								<Typography.Title level={3} style={{ fontSize: '16px' }}>
									Add New Company
								</Typography.Title>

								<Text strong style={{ fontSize: '12px' }}>
									Please select the account to add the company.
								</Text>
								{accounts.map((acc) => (
									<div
										key={acc.AccountUid}
										onClick={() => handleSelectChange(acc)}
										style={{
											cursor: 'pointer',
											textDecoration: 'underline',
											color: '#344735',
											fontSize: '12px',
											fontWeight: selectedUid === acc.uid ? 'bold' : 'normal',
											marginTop: '10px',
										}}
									>
										{acc.AccountName}
									</div>
								))}
							</span>
						) : (
							<>
								{selectedAccount.IsPrimary ? (
									// Primary account selected
									selectedAccount.hasNewPlan ? (
										// Primary account with new plan - show confirmation message
										<span
											style={{
												maxHeight: 'vh', // Adjust this value as needed
												overflowY: 'auto',
												paddingRight: '10px', // optional: adds space to avoid content hiding behind scrollbar
											}}
										>
											{' '}
											<Typography.Title level={3} style={{ fontSize: '16px' }}>
												Add New Company
											</Typography.Title>
											<Text strong style={{ fontSize: '12px' }}>
												Adding a new company will increase the subscription
												quantity for your account billing.
											</Text>
											<Text style={{ fontSize: '12px' }}
											>
												Visit our{' '}
												<Link
													onClick={() =>
														window.open(
															'https://costallocationpro.outseta.com/support/kb/articles/79O3M49E/how-does-our-pricing-work-',
															'_blank'
														)
													}
													style={{
														fontWeight: 'bold',
														textDecoration: 'underline',
														color: '#344735',
														fontSize: '12px',
													}}
												>
													knowledge base
												</Link>{' '}
												for subscription details.
											</Text>
										</span>
									) : (
										// Primary account with old plan - show upgrade message
										<span
											style={{
												maxHeight: 'vh', // Adjust this value as needed
												overflowY: 'auto',
												paddingRight: '10px', // optional: adds space to avoid content hiding behind scrollbar
											}}
										>
											<Typography.Title level={4} style={{ fontSize: '16px' }}>
												Subscription Plan Update Required
											</Typography.Title>
											<Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '16px' }}>
												This account is on an older subscription plan. To add a new company,
												please select an account with the updated subscription plan or contact
												support to upgrade your current plan.
											</Text>
											<Button
												type="link"
												onClick={handleBack}
												style={{
													marginTop: '16px',
													padding: 0,
													color: '#344735',
													display: 'block',
												}}
											>
												<ArrowLeftOutlined /> Back to account list
											</Button>
										</span>
									)
								) : (
									// Non-primary account (any plan) - show not owner message
									<span
										style={{
											maxHeight: '100vh', // Adjust this value as needed
											overflowY: 'auto',
											paddingRight: '10px', // optional: adds space to avoid content hiding behind scrollbar
											display: 'flex',
											flexDirection: 'column',
										}}
									>
										<Typography.Title level={4} style={{ fontSize: '16px' }}>
											Sorry!
										</Typography.Title>
										<Text strong style={{ fontSize: '12px' }}>
											It looks like you are not the account owner. To add a
											company to this account, please reach out to{' '}
											{
												<Link
													href={`mailto:${selectedAccount.PrimaryContactEmail}`}
												>
													{selectedAccount.PrimaryContactEmail}
												</Link>
											}
											.
										</Text>
										<Button
											type="link"
											onClick={handleBack}
											style={{
												padding: 0,
												color: '#344735',
												alignSelf: 'flex-start',
											}}
										>
											<ArrowLeftOutlined /> Back to account list
										</Button>
									</span>
								)}
							</>
						)}
					</>
				)}

				{/* Footer buttons */}
				<Space
					style={{
						width: '100%',
						justifyContent: 'flex-end',
						marginTop: '0px',
					}}
				>
					<Button
						onClick={onCancel}
						size="large"
						className={style['custom-cancel-button']}
					>
						Cancel
					</Button>

					{selectedAccount?.IsPrimary && selectedAccount?.hasNewPlan && (
						<Button
							type="primary"
							onClick={handleConfirm}
							style={{
								backgroundColor: '#344735',
								color: '#fff',
								fontSize: '12px',
								borderRadius: '12px',
							}}
							size="large"
						>
							Continue
						</Button>
					)}
				</Space>
			</Space>
		</Modal>
	);
};

export default ConfirmationModal;
